import {computed} from "vue";
import {AuthStore} from "@/store/modules/auth";
import {UserStore} from "@/store/modules/user";
import {EnumRightSetRole} from "@/enum/right";
import {useMenu} from "@/hook/useMenu";
import router from "@/router";

/**
 * 判斷權限
 */
export const usePermission = () => {

    const userStore = UserStore()

    const authStore = AuthStore()

    /**
     * 是否為模擬登入
     *
     * church.simulation = 模擬前教會 uuid，有值才是模擬登入。
     * 後端 get() 改 ok()（Gson/Json2Utils 會省略 null 鍵）後，一般登入時此欄位整個 key 會消失
     * → 值為 undefined；原 !["", null].includes(...) 對 undefined 會誤判為 true，使一般教會
     * 登入被當成總會模擬登入。改用 truthy 判斷同時涵蓋 null / undefined / "" 三種「非模擬」狀態。
     */
    const isSimulation = computed(() =>{
        return !!userStore.getAll.church.simulation
    })

    /**
     * 是否為總教會登入
     */
    const isHead = computed(() =>{
        return userStore.getAll.church.head
    })

    /**
     * 是否具有權限 (單個權限)
     */
    const isHasRight = computed(() => {
        return (e) => {
            return authStore.getPermission.includes(e)
        }
    })

    /**
     * 是否登入（權杖在 httpOnly cookie，這裡只看登入旗標）
     */
    const isLogin = computed(() => authStore.isLogin)

    /**
     * 登入 / 登出
     */
    const setLogin = (e) => authStore.setLogin(e)

    /**
     * 身分變動（模擬登入 / 回總會 / 登出）：新的權杖已由後端寫進 cookie
     * @param e 是否登入
     */
    const updateLogin = (e) => {

        const {
            setOverlay
        } = useMenu()

        //如果在最小視窗, 縮起
        setOverlay(false)

        setLogin(e)

        //故意丟回登入頁面, 他會跳轉回控制面板
        setTimeout(async () => await router.replace({
            name: `Login`,
        }), 0)

    }

    /**
     * 返回目前的權限
     */
    const getPermission = () => {
        return authStore.getPermission
    }

    /**
     * 一次性注入權限資料
     */
    const setPermission = (e) => authStore.setPermission(e)

    /**
     * 設定用的權限表
     * @param head 是否為總教會
     * @param group 傳下來可用的權限群組
     * @param available 傳下來「啟用中」的權限代號; 不在其中的欄位會被視為 -1 (不顯示勾選框)
     *                  不傳 (null) 則不過濾, 維持原行為
     * Permission.role 總教會抓 [HEAD, ALL], 一般教會抓 [NORMAL, ALL]
     *
     * ⚠️ 本表是硬編碼的, 不知道後端權限範本是否被停用 (core_admin_permission_template._s=0);
     *    而停用的項目「勾得到、也會寫進DB, 但讀不回來也不會生效」(讀取端與權限檢查都濾 _s=1),
     *    所以要靠 available 把已停用的欄位隱藏掉, 避免使用者勾了卻存不住。
     */
    const getAvailableRight = (head,
                               group,
                               available = null) => {

        const r = [{
            //我的教會 - 控制面板
            seq: 100,
            portal: 1,
            group: 1,
            role: EnumRightSetRole.ALL,
            t: `nav.admin.home.permission`,
            feature: [1001, -1, -1, -1, -1, -1, -1]
        }, {
            //我的教會 - 公告管理
            portal: 1,
            group: 17,
            role: EnumRightSetRole.HEAD,
            t: `nav.admin.billboard.permission`,
            feature: [17001, 17002, 17003, 17004, 17005, 17006, -1]
        }, {
            //我的教會 - 管理員帳號管理
            portal: 1,
            group: 2,
            role: EnumRightSetRole.ALL,
            t: `nav.admin.user.permission`,
            feature: [2001, 2002, 2003, 2004, 2005, 2006, -1]
        }, {
            //我的教會 - 教會管理
            portal: 1,
            group: 3,
            role: EnumRightSetRole.ALL,
            t: `nav.admin.church.permission`,
            feature: !head ?
                [-1, -1, -1, 3004, 3005, -1, -1]:
                [3001, 3002, 3003, 3004, 3005, -1, -1]
        }, {
            //我的教會 - 會友管理
            portal: 1,
            group: 4,
            role: EnumRightSetRole.ALL,
            t: `nav.admin.client.permission`,
            feature: [4001, -1, -1, 4004, 4005, 4006, 4007]
        }, {
            //我的教會 - 會友管理 - 奉獻紀錄
            portal: 1,
            group: 4,
            role: EnumRightSetRole.ALL,
            t: `nav.admin.client.donate.permission`,
            feature: [4011, -1, -1, 4014, -1, -1, 4017]
        }, {
            //我的教會 - 我的教會文章管理
            portal: 1,
            group: 5,
            role: EnumRightSetRole.ALL,
            t: `nav.admin.my.permission`,
            feature: [5001, 5002, 5003, 5004, 5005, 5006, -1]
        }, {
            //我的教會 - 我的教會文章管理 - 留言紀錄
            portal: 1,
            group: 5,
            role: EnumRightSetRole.ALL,
            t: `nav.admin.my.comment.permission`,
            feature: [5011, -1, -1, -1, -1, 5016, -1]
        }, {
            //我的教會 - 我的教會文章管理 - 訊息通知
            portal: 1,
            group: 5,
            role: EnumRightSetRole.ALL,
            t: `nav.admin.my.notification.permission`,
            feature: [5021, 5022, 5023, 5024, 5025, 5026, -1]
        }, {
            //我的教會 - 心靈捕手文章管理
            portal: 1,
            group: 6,
            role: EnumRightSetRole.ALL,
            t: `nav.admin.soul.permission`,
            feature: [6001, 6002, 6003, 6004, 6005, 6006, -1]

        }, {
            //我的教會 - 心靈捕手文章管理 - 分類管理
            portal: 1,
            group: 6,
            role: EnumRightSetRole.ALL,
            t: `nav.admin.soul.category.permission`,
            feature: [6011, 6012, 6013, 6014, 6015, 6016, -1]
        }, {
            //我的教會 - 心靈捕手文章管理 - 留言紀錄
            portal: 1,
            group: 6,
            role: EnumRightSetRole.ALL,
            t: `nav.admin.soul.comment.permission`,
            feature: [6021, -1, -1, -1, -1, 6026, -1]
        }, {
            //我的教會 - 心靈捕手文章管理 - 訊息通知
            portal: 1,
            group: 6,
            role: EnumRightSetRole.ALL,
            t: `nav.admin.soul.notification.permission`,
            feature: [6031, 6032, 6033, 6034, 6035, 6036, -1]
        }, {
            //我的教會 - 每日靈糧文章管理
            portal: 1,
            group: 7,
            role: EnumRightSetRole.ALL,
            t: `nav.admin.daily.permission`,
            feature: [7001, 7002, 7003, 7004, 7005, 7006, -1]
        }, {
            //我的教會 - 每日靈糧文章管理 - 留言紀錄
            portal: 1,
            group: 7,
            role: EnumRightSetRole.ALL,
            t: `nav.admin.daily.comment.permission`,
            feature: [7011, -1, -1, -1, -1, 7016, -1]
        }, {
            //我的教會 - 每日靈糧文章管理 - 訊息通知
            portal: 1,
            group: 7,
            role: EnumRightSetRole.ALL,
            t: `nav.admin.daily.notification.permission`,
            feature: [7021, 7022, 7023, 7024, 7025, 7026, -1]
        }, {
            //我的教會 - 圖庫管理 - 愛無界圖庫
            portal: 1,
            group: 8,
            role: EnumRightSetRole.HEAD,
            t: `nav.admin.gallery.permission.index`,
            feature: [8001, -1, -1, -1, 8005, -1, -1]
        }, {
            //我的教會 - 圖庫管理 - 圖庫核准
            portal: 1,
            group: 8,
            role: EnumRightSetRole.HEAD,
            t: `nav.admin.gallery.permission.accept`,
            feature: [8011, -1, -1, -1, 8015, -1, -1]
        }, {
            //我的教會 - 圖庫管理 - 圖庫檢舉
            portal: 1,
            group: 8,
            role: EnumRightSetRole.HEAD,
            t: `nav.admin.gallery.permission.alarm`,
            feature: [8021, -1, -1, -1, 8025, -1, -1]
        }, {
            //我的教會 - 推播管理
            portal: 1,
            group: 9,
            role: EnumRightSetRole.ALL,
            t: `nav.admin.notification.permission`,
            feature: [9001, 9002, 9003, 9004, 9005, 9006, -1]
        }, {
            //我的教會 - 服事管理
            portal: 1,
            group: 10,
            role: EnumRightSetRole.ALL,
            t: `nav.admin.event.permission`,
            feature: [10001, 10002, 10003, 10004, 10005, 10006, -1]
        }, {
            //我的教會 - 服事管理 - 服事群組
            portal: 1,
            group: 10,
            role: EnumRightSetRole.ALL,
            t: `nav.admin.event.team.permission`,
            feature: [10011, 10012, 10013, 10014, 10015, 10016, -1]
        }, {
            //我的教會 - 教會行事曆
            //⚠️ 這張表是寫死的：資料庫的 core_admin_permission_template 只決定後端擋不擋，
            //   權限設定畫面列出什麼完全看這個陣列。新增權限模組時兩邊都要加。
            portal: 1,
            group: 21,
            role: EnumRightSetRole.ALL,
            t: `nav.admin.calendar.permission`,
            feature: [21001, 21002, 21003, 21004, 21005, 21006, -1]
        }, {
            //我的教會 - 奉獻管理
            portal: 1,
            group: 11,
            role: EnumRightSetRole.ALL,
            t: `nav.admin.donate.permission`,
            feature: [11001, -1, -1, 11004, -1, -1, 11007]
        }, {
            //我的教會 - 教學管理
            portal: 1,
            group: 12,
            role: EnumRightSetRole.HEAD,
            t: `nav.admin.teach.permission`,
            feature: [12001, 12002, 12003, 12004, 12005, 12006, -1]
        }, {
            //我的教會 - 啟動圖排程
            portal: 1,
            group: 20,
            role: EnumRightSetRole.HEAD,
            t: `nav.admin.splash.permission`,
            feature: [20001, 20002, 20003, 20004, 20005, 20006, -1]
        }, {
            //我的教會 - 循環管理
            portal: 1,
            group: 13,
            role: EnumRightSetRole.ALL,
            t: `nav.admin.cycle.permission`,
            feature: [13001, 13002, 13003, 13004, 13005, 13006, -1]
        }, {
            //我的教會 - 活動管理
            portal: 1,
            group: 18,
            role: EnumRightSetRole.ALL,
            t: `nav.admin.activity.permission`,
            feature: [18001, 18002, 18003, 18004, 18005, 18006, 18007]
        }, {
            //我的教會 - 活動管理 - 分類管理
            portal: 1,
            group: 18,
            role: EnumRightSetRole.ALL,
            t: `nav.admin.activity.category.permission`,
            feature: [18011, 18012, 18013, 18014, 18015, 18016, -1]
        }, {
            //我的教會 - 直播管理
            portal: 1,
            group: 19,
            role: EnumRightSetRole.ALL,
            t: `nav.admin.live.permission`,
            feature: [19001, 19002, 19003, 19004, 19005, 19006, -1]
        }, {
            //我的教會 - 直播管理 - 分類管理
            portal: 1,
            group: 19,
            role: EnumRightSetRole.ALL,
            t: `nav.admin.live.category.permission`,
            feature: [19011, 19012, 19013, 19014, 19015, 19016, -1]
        }, {
            //我的教會 - 客服管理 - 帳號紀錄
            portal: 1,
            group: 15,
            role: EnumRightSetRole.HEAD,
            t: `nav.admin.support.history.permission`,
            feature: [15001, -1, -1, 15004, -1, -1, -1]
        }, {
            //我的教會 - 客服管理 - 意見與建議
            portal: 1,
            group: 15,
            role: EnumRightSetRole.ALL,
            t: `nav.admin.support.feedback.permission`,
            feature: [15011, -1, -1, 15014, 15015, -1, -1]
        }, {
            //我的教會 - 客服管理 - 請我們喝咖啡
            portal: 1,
            group: 15,
            role: EnumRightSetRole.HEAD,
            t: `nav.admin.support.coffee.permission`,
            feature: [15021, -1, -1, 15024, -1, -1, 15027]
        }, {
            //我的教會 - 歷程管理 - 攔截紀錄
            portal: 1,
            group: 16,
            role: EnumRightSetRole.HEAD,
            t: `nav.admin.course.exception.permission`,
            feature: [16001, -1, -1, 16004, -1, -1, -1]
        }, {
            //我的教會 - 歷程管理 - 存取紀錄
            portal: 1,
            group: 16,
            role: EnumRightSetRole.HEAD,
            t: `nav.admin.course.access.permission`,
            feature: [16011, -1, -1, 16014, -1, -1, -1]
        }, {
            //我的教會 - 歷程管理 - 授權紀錄
            portal: 1,
            group: 16,
            role: EnumRightSetRole.HEAD,
            t: `nav.admin.course.authorize.permission`,
            feature: [16021, -1, -1, 16024, -1, -1, -1]
        }, {
            //我的教會 - App 錯誤追蹤（僅總會；只啟用 檢視/編輯/更新 三項）
            portal: 1,
            group: 22,
            role: EnumRightSetRole.HEAD,
            t: `nav.admin.appError.permission`,
            feature: [22001, -1, -1, 22004, 22005, -1, -1]
        }, {
            //牧養系統 - 控制面板
            portal: 2,
            group: 51,
            role: EnumRightSetRole.ALL,
            t: `nav.shepherd.home.permission`,
            feature: [51001, -1, -1, -1, -1, -1, -1]
        }, {
            //牧養系統 - 新朋友管理
            portal: 2,
            group: 52,
            role: EnumRightSetRole.ALL,
            t: `nav.shepherd.friend.permission`,
            feature: [52001, 52002, 52003, 52004, 52005, 52006, -1]
        }, {
            //牧養系統 - 會友管理
            portal: 2,
            group: 53,
            role: EnumRightSetRole.ALL,
            t: `nav.shepherd.client.permission`,
            feature:  [53001, 53002, 53003, 53004, 53005, 53006, 53007]
        }/*, {
                //牧養系統 - 會友管理 - 進階功能
                portal: 2,
                group: 53,
                role: EnumRightSetRole.ALL,
                t: `nav.shepherd.client.advance.permission`,
                feature: [53011, -1, -1, -1, 53006, 53007, -1]
        }*/, {
            //牧養系統 - 族群管理
            portal: 2,
            group: 54,
            role: EnumRightSetRole.ALL,
            t: `nav.shepherd.team.permission`,
            feature: [54001, 54002, 54003, 54004, 54005, 54006, -1]
        }, {
            //牧養系統 - 聚會管理
            portal: 2,
            group: 55,
            role: EnumRightSetRole.ALL,
            t: `nav.shepherd.attend.permission`,
            feature: [55001, 55002, 55003, 55004, 55005, 55006, 55007]
        }, {
            //牧養系統 - 聚會管理 - 分類管理
            portal: 2,
            group: 55,
            role: EnumRightSetRole.ALL,
            t: `nav.shepherd.attend.category.permission`,
            feature: [55011, 55012, 55013, 55014, 55015, 55016, -1]
        }, {
            //牧養系統 - 參數管理
            portal: 2,
            group: 56,
            role: EnumRightSetRole.NORMAL,
            t: `nav.shepherd.setting.permission`,
            feature: !head ?
                [-1, -1, -1, 56004, 56005, -1, -1]:
                [-1, -1, -1, -1, -1, -1, -1]
        }, {
            //榮耀堂首頁 - 控制面板
            //
            //以下 9 個群組 (61~69) 是 org=4 專屬。這裡不做 org 判斷 ——
            //後端 dao_admin_user.xml 的權限查詢已把 portal=3 的群組限定給 organization.seq=4,
            //非 org=4 的 group 陣列裡不會有 61~69, 下面的 filter 自然會把它們全部濾掉。
            portal: 3,
            group: 61,
            role: EnumRightSetRole.NORMAL,
            t: `nav.glory.home.permission`,
            feature: [61001, -1, -1, -1, -1, -1, -1]
        }, {
            //榮耀堂首頁 - 資訊設定 (一教會一列的單頁表單, 沒有新增與刪除)
            portal: 3,
            group: 62,
            role: EnumRightSetRole.NORMAL,
            t: `nav.glory.info.permission`,
            feature: [62001, -1, -1, 62004, 62005, -1, -1]
        }, {
            //榮耀堂首頁 - 團隊管理
            portal: 3,
            group: 63,
            role: EnumRightSetRole.NORMAL,
            t: `nav.glory.team.permission`,
            feature: [63001, 63002, 63003, 63004, 63005, 63006, -1]
        }, {
            //榮耀堂首頁 - 精選分類
            portal: 3,
            group: 64,
            role: EnumRightSetRole.NORMAL,
            t: `nav.glory.featured.permission`,
            feature: [64001, 64002, 64003, 64004, 64005, 64006, -1]
        }, {
            //榮耀堂首頁 - 精選訊息
            portal: 3,
            group: 65,
            role: EnumRightSetRole.NORMAL,
            t: `nav.glory.message.permission`,
            feature: [65001, 65002, 65003, 65004, 65005, 65006, -1]
        }, {
            //榮耀堂首頁 - 敬拜管理
            portal: 3,
            group: 66,
            role: EnumRightSetRole.NORMAL,
            t: `nav.glory.worship.permission`,
            feature: [66001, 66002, 66003, 66004, 66005, 66006, -1]
        }, {
            //榮耀堂首頁 - 活動回顧
            portal: 3,
            group: 67,
            role: EnumRightSetRole.NORMAL,
            t: `nav.glory.event.permission`,
            feature: [67001, 67002, 67003, 67004, 67005, 67006, -1]
        }, {
            //榮耀堂首頁 - 關鍵字管理
            portal: 3,
            group: 68,
            role: EnumRightSetRole.NORMAL,
            t: `nav.glory.keyword.permission`,
            feature: [68001, 68002, 68003, 68004, 68005, 68006, -1]
        }, {
            //榮耀堂首頁 - 接受耶穌登錄 (決志表單的收件匣, 只能看與改狀態)
            portal: 3,
            group: 69,
            role: EnumRightSetRole.NORMAL,
            t: `nav.glory.accept.permission`,
            feature: [69001, -1, -1, 69004, 69005, -1, -1]
        }]

        //總教會抓 [1, 3], 一般教會抓 [2, 3]
        //const role = ( isHead.value ? [EnumRightSetRole.HEAD] : [EnumRightSetRole.NORMAL] ).concat([EnumRightSetRole.ALL])

        //篩掉個別才有的功能表
        return r
            .filter(i=>{

                if (i.group === 15) console.log(head, i)

                //不在下傳的權限群組中
                if (!group.includes(i.group)) return false
                //如果標示全部都有
                else if (i.role === EnumRightSetRole.ALL) return true
                //如果標示總教會才有
                else if (head && i.role === EnumRightSetRole.HEAD) return true
                //如果標示非總教會才有
                else if (!head && i.role === EnumRightSetRole.NORMAL) return true

                return false

            })
            .map(i=>{
                return Object.assign(i, {
                    seq: i.group * 100,
                    //已停用的權限範本 → 比照原本的 -1 (該欄位不顯示勾選框)
                    feature: available === null ? i.feature : i.feature.map(f => f > 0 && !available.includes(f) ? -1 : f)
                });
            })

    }

    return {
        isSimulation,
        isHead,
        isLogin,
        isHasRight,
        setLogin,
        updateLogin,
        getPermission,
        setPermission,
        getAvailableRight
    }

}
